import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { View } from '@react-pdf/renderer';
import OpportunitiesIcon from 'PDF/assets/icons/OpportunitiesIcon.png';
import StrengthsIcon from 'PDF/assets/icons/StrengthsIcon.png';
import ThreatsIcon from 'PDF/assets/icons/ThreatsIcon.png';
import WeaknessIcon from 'PDF/assets/icons/WeaknessesIcon.png';
import { globalStyles } from 'PDF/styles';

import { SWOTRow } from './SWOTRow';
import { SubChapterHeading } from '../SubChapterHeading';

interface IStep {
  title: string;
}

interface ISWOTObject {
  title: string;
  iconSrc: string;
  content: string[];
}

interface ISwotAnalysisProps {
  data: { [p: string]: any };
  heading: string;
  isExamplePage: boolean;
}

export const SWOT: React.FC<ISwotAnalysisProps> = ({ data, heading, isExamplePage }) => {
  const { t, i18n } = useTranslation();

  const swotContentMap = useMemo(
    () => ({
      opportunities: {
        title: isExamplePage ? 'Opportunities' : t('opportunities'),
        icon: OpportunitiesIcon,
      },
      companyStrengths: {
        title: isExamplePage ? 'Strengths' : t('strengths'),
        icon: StrengthsIcon,
      },
      threats: {
        title: isExamplePage ? 'Threats' : t('threats'),
        icon: ThreatsIcon,
      },
      companyWeaknesses: {
        title: isExamplePage ? 'Weaknesses' : t('weaknesses'),
        icon: WeaknessIcon,
      },
    }),
    [t, i18n],
  );
  const SWOTInfo = (Object.keys(swotContentMap) as Array<keyof typeof swotContentMap>)
    .map((key) => {
      const content = data[key].map((step: IStep) => step.title);

      return {
        title: swotContentMap[key].title,
        content,
        iconSrc: swotContentMap[key].icon,
      };
    })
    .reduce(
      (acc: [ISWOTObject[], ISWOTObject[]], obj) => {
        if (
          obj.title === t('strengths') ||
          obj.title === 'Strengths' ||
          obj.title === t('weaknesses') ||
          obj.title === 'Weaknesses'
        ) {
          acc[0].push(obj);
        } else {
          acc[1].push(obj);
        }
        return acc;
      },
      [[], []],
    );
  const swotInfoFirstElement = SWOTInfo[0];
  const swotInfoSecondElements = SWOTInfo[1];

  return (
    <View style={globalStyles.marginBottom}>
      <View wrap={false}>
        <SubChapterHeading heading={heading} id="swotAnalysis" />
        <SWOTRow data={swotInfoFirstElement} isSecond={false} />;
      </View>
      <SWOTRow data={swotInfoSecondElements} isSecond={true} />;
    </View>
  );
};
